import { c as _c } from "react/compiler-runtime";
import { tagMarkdownForRender, tagUrlsForRender } from '@shared/all/contentFilter/contentFilter';
import { internalPath } from '@shared/all/helpers/internalLink';
import usePreference from '@shared/utils/hooks/usePreference';
import type DOMPurifyType from 'dompurify';
import Link from 'next/link';
import React, { useRef } from 'react';
import ReactHtmlParser, { type Transform } from 'react-html-parser';
import InternalLinkCard from '../components/UI/InternalLinkCard';
import LinkPreview from '../components/UI/LinkPreview';
import { client } from '../utils/trpc';

// Type for DOM elements from react-html-parser (matches htmlparser2 DomElement)
interface HtmlParserNode {
  type?: string;
  data?: string;
  name?: string;
  attribs?: Record<string, string>;
  children?: HtmlParserNode[];
}

// Dynamic import for DOMPurify to avoid SSR issues
let DOMPurify: typeof DOMPurifyType | null = null;
if (typeof window !== 'undefined') {
  // Only import DOMPurify on the client side
  void import('dompurify').then(module => {
    DOMPurify = module.default;
  });
}

// DOMPurify configuration - clean and secure
const sanitizeConfig = {
  ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'div', 'span', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'mark', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'pre', 'code', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'hr',
  // Custom app tags
  'url', 'mention', 's', 'f', 'c'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'class', 'id', 'factiiiId'],
  ALLOW_DATA_ATTR: false,
  FORBID_SCRIPT: true,
  FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
  FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'style']
};

// Code sits on the page canvas rather than the card's own surface, which is
// only a shade away from it. Shared because code renders down two paths — the
// style map below for plain <code>/<pre> in pasted or imported HTML, and the
// transform for the write-time tagged form — and they had drifted apart.
const CODE_SURFACE = 'border border-ds-border-standard bg-background-primary';
const CODE_INLINE = `${CODE_SURFACE} rounded px-1.5 py-0.5 font-mono text-[0.9em] text-textPrimary`;

// CSS classes for HTML elements
const getElementStyles = (tagName: string): string => {
  const styles: Record<string, string> = {
    h1: 'text-2xl md:text-3xl font-semibold text-textPrimary mb-4 mt-6 first:mt-0',
    h2: 'text-xl md:text-2xl font-semibold text-textPrimary mb-3 mt-5 first:mt-0',
    h3: 'text-lg md:text-xl font-semibold text-textPrimary mb-3 mt-4 first:mt-0',
    h4: 'text-base md:text-lg font-semibold text-textPrimary mb-2 mt-3 first:mt-0',
    h5: 'text-sm md:text-base font-semibold text-textPrimary mb-2 mt-3 first:mt-0',
    h6: 'text-sm font-semibold text-textPrimary mb-2 mt-2 first:mt-0',
    p: 'text-textPrimary mb-4 leading-relaxed',
    // Markers sit outside so a nested list lines up under its parent's text,
    // and each level changes shape the way Slack's do (disc / circle / square,
    // and 1. / a. / i.). The nested rules live on the outer list because that
    // is the only element whose class survives to the DOM.
    ul: 'text-textPrimary mb-4 ml-6 space-y-2 list-disc list-outside [&_ul]:mb-0 [&_ul]:ml-5 [&_ul]:mt-2 [&_ul]:list-[circle] [&_ul_ul]:list-[square]',
    ol: 'text-textPrimary mb-4 ml-6 space-y-2 list-decimal list-outside [&_ol]:mb-0 [&_ol]:ml-5 [&_ol]:mt-2 [&_ol]:list-[lower-alpha] [&_ol_ol]:list-[lower-roman]',
    li: 'text-textPrimary leading-relaxed',
    // A left bar only, the way Slack and Trello mark a quote. A background fill
    // reads as a full-width slab, which is what a code block looks like.
    blockquote: 'border-l-[3px] border-primary pl-4 my-3 text-textSecondary italic',
    strong: 'font-semibold text-textPrimary',
    b: 'font-semibold text-textPrimary',
    em: 'italic text-textPrimary',
    i: 'italic text-textPrimary',
    u: 'underline text-textPrimary',
    s: 'line-through text-textSecondary',
    del: 'line-through text-textSecondary',
    mark: 'bg-yellow-200 px-1 rounded text-textPrimary',
    a: 'text-primary hover:text-secondary underline transition-colors duration-200',
    pre: `${CODE_SURFACE} mb-4 overflow-x-auto rounded-md p-4 font-mono text-sm text-textPrimary`,
    code: CODE_INLINE,
    table: 'min-w-full border-collapse border border-ds-border-standard mb-4 table-auto text-sm',
    thead: 'bg-background-secondary',
    th: 'border border-ds-border-standard px-2 py-2 md:px-4 text-left font-semibold text-textPrimary text-xs md:text-sm whitespace-nowrap',
    td: 'border border-ds-border-standard px-2 py-2 md:px-4 text-textPrimary text-xs md:text-sm',
    tr: 'hover:bg-background-light transition-colors duration-150',
    hr: 'border-0 border-t border-ds-border-standard my-6',
    img: 'max-w-full h-auto rounded-md mb-4'
  };
  return styles[tagName] || '';
};
const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://factiii.com';
const useContentUIRenderer = () => {
  const $ = _c(3);
  const {
    preferenceState
  } = usePreference(client);
  const urlPreviewRef = useRef("");
  const inAppPreviewRef = useRef("");
  const inAppLinksRef = useRef(false);
  const currentContentIdRef = useRef("");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = href => inAppLinksRef.current ? internalPath(href, SITE_URL) : null;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const inAppPathOf = t0;
  let t1;
  if ($[1] !== preferenceState) {
    const transform = (node, index) => {
      if (node.type === "tag") {
        const {
          name,
          attribs: t2,
          children
        } = node;
        const attribs = t2 === undefined ? {} : t2;
        if (name === "url" && attribs.id) {
          const url = children?.[0]?.data || "";
          const tagId = attribs.id;
          if (tagId === currentContentIdRef.current.slice(0, 1)) {
            const href_0 = /^(http:|https:)?\/\//.test(url) ? url : `https://${url}`;
            const inAppPath = inAppPathOf(href_0);
            if (inAppPath) {
              inAppPreviewRef.current = inAppPath;
              return <Link key={index} href={inAppPath} className="text-primary underline hover:text-secondary" onClick={_temp}>{url}</Link>;
            }
            urlPreviewRef.current = href_0;
            return <a key={index} href={href_0} target="_blank" rel="noopener noreferrer nofollow ugc" className="text-primary underline hover:text-secondary">{url}</a>;
          }
          return <span key={index} className="text-textPrimary">{url}</span>;
        }
        if (name === "mention" && attribs.id) {
          const username = children?.[0]?.data || "";
          const tagId_0 = attribs.id;
          if (tagId_0 === currentContentIdRef.current.slice(0, 1)) {
            const cleanUsername = username.startsWith("@") ? username.slice(1) : username;
            return <Link key={index} href={`/user/${cleanUsername}`} className="font-semibold text-primary hover:text-secondary" onClick={_temp2}>{username}</Link>;
          }
          return <span key={index} className="text-textPrimary">{username}</span>;
        }
        if (name === "s" && attribs.id) {
          const spaceName = children?.[0]?.data || "";
          const tagId_1 = attribs.id;
          if (tagId_1 === currentContentIdRef.current.slice(0, 1)) {
            const cleanSpaceName = spaceName.startsWith("s/") ? spaceName.slice(2) : spaceName;
            return <Link key={index} href={`/s/${cleanSpaceName}`} className="font-semibold text-primary hover:text-secondary" onClick={_temp3}>{spaceName}</Link>;
          }
          return <span key={index} className="text-textPrimary">{spaceName}</span>;
        }
        if (name === "f" && attribs.id && attribs.factiiiId) {
          const factiiiName = children?.[0]?.data || "";
          const tagId_2 = attribs.id;
          const factiiiId = attribs.factiiiId;
          if (tagId_2 === currentContentIdRef.current.slice(0, 1)) {
            return <Link key={index} href={`/fids/${factiiiId}`} className="font-semibold text-primary hover:text-secondary" onClick={_temp4}>{factiiiName}</Link>;
          }
          return <span key={index} className="text-textPrimary">{factiiiName}</span>;
        }
        if (name === "code" && attribs.id) {
          const code = children?.[0]?.data || "";
          if (attribs.id !== currentContentIdRef.current.slice(0, 1)) {
            return <span key={index} className="text-textPrimary">{code}</span>;
          }
          return code.includes("\n") ? <pre key={index} className={`${CODE_SURFACE} my-2 overflow-x-auto rounded-md p-3`}><code className="font-mono text-sm text-textPrimary">{code}</code></pre> : <code key={index} className={CODE_INLINE}>{code}</code>;
        }
        if (name === "c" && attribs.id) {
          const word = children?.[0]?.data || "";
          const tagId_3 = attribs.id;
          if (tagId_3 === currentContentIdRef.current.slice(0, 1) && !preferenceState.allowProfanity) {
            return <span key={index} className="text-textPrimary">{"*".repeat(word.length)}</span>;
          }
          return <span key={index} className="text-textPrimary">{word}</span>;
        }
        const styleClass = getElementStyles(name);
        if (name === "a" && attribs.href) {
          const inAppPath_0 = inAppPathOf(attribs.href);
          if (inAppPath_0) {
            return <Link key={index} href={inAppPath_0} className={styleClass} onClick={_temp5}>{children?.map(_temp6)}</Link>;
          }
          return <a key={index} href={attribs.href} target={attribs.target || "_blank"} rel={attribs.rel || "noopener noreferrer nofollow ugc"} className={styleClass} onClick={_temp7}>{children?.map(_temp8)}</a>;
        }
        if (name === "table") {
          const tableClasses = `${styleClass} mb-0 w-full`;
          attribs.class = `${tableClasses} ${attribs.class || ""}`.trim();
          attribs.style = "min-width: 500px";
          return;
        }
        if (styleClass) {
          const existingClasses = attribs.class || "";
          attribs.class = `${styleClass} ${existingClasses}`.trim();
        }
      }
    };
    const contentUIRenderHandler = (rawContent, id, isFullLength, options) => {
      currentContentIdRef.current = id;
      urlPreviewRef.current = "";
      inAppPreviewRef.current = "";
      inAppLinksRef.current = options?.inAppLinks ?? false;
      const maxContentLength = isFullLength ? Infinity : options?.maxLength ?? 300;
      const visible = rawContent.length > maxContentLength ? rawContent.slice(0, maxContentLength).replace(/<[^>]*$/, "") : rawContent;
      const urlTagged = options?.autoTagUrls ? tagUrlsForRender(visible, id) : visible;
      const preprocessedContent = tagMarkdownForRender(urlTagged, id).replaceAll("<br />", "<br/>").replaceAll("<br/>", "\n").replace(/<@\s/g, "<mention ").replace(/<\/@>/g, "</mention>").replace(/<link\s/g, "<url ").replace(/<\/link>/g, "</url>");
      const contentForParsing = DOMPurify ? DOMPurify.sanitize(preprocessedContent, sanitizeConfig) : preprocessedContent;
      const elements = ReactHtmlParser(contentForParsing, {
        transform
      });
      const processedElements = elements.map(_temp9);
      return <div className="break-words text-textPrimary" style={{
        whiteSpace: "pre-wrap"
      }}>{processedElements}{!options?.hideViewMore && rawContent.length > maxContentLength && !isFullLength && <span>...view more.</span>}{!options?.hideLinkPreview && !inAppPreviewRef.current && urlPreviewRef.current && <LinkPreview url={urlPreviewRef.current} compact={options?.compactLinkPreview} />}{!options?.hideLinkPreview && inAppPreviewRef.current && <InternalLinkCard path={inAppPreviewRef.current} />}</div>;
    };
    const filterText = _temp10;
    const removeTags = _temp11;
    t1 = {
      contentUIRenderHandler,
      filterText,
      removeTags
    };
    $[1] = preferenceState;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export default useContentUIRenderer;
function _temp(e) {
  return e.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp3(e_1) {
  return e_1.stopPropagation();
}
function _temp4(e_2) {
  return e_2.stopPropagation();
}
function _temp5(e_3) {
  return e_3.stopPropagation();
}
function _temp6(child) {
  return child.type === "text" ? child.data : null;
}
function _temp7(e_4) {
  return e_4.stopPropagation();
}
function _temp8(child_0) {
  return child_0.type === "text" ? child_0.data : null;
}
function _temp9(element, index_0) {
  if (React.isValidElement(element) && element.type === "table") {
    return <div key={index_0} className="-mx-2 mb-4 overflow-x-auto rounded-lg border border-ds-border-standard sm:mx-0">{element}</div>;
  }
  return element;
}
function _temp10(t0) {
  const {
    content,
    isFullLength: isFullLength_0
  } = t0;
  const maxContentLength_0 = isFullLength_0 ? 5000 : 300;
  const rawContent_0 = content.slice(0, maxContentLength_0);
  if (!DOMPurify) {
    return rawContent_0.replaceAll("<br />", " \n").replaceAll("<br/>", "\n");
  }
  return DOMPurify.sanitize(rawContent_0, {
    FORBID_TAGS: ["url", "@", "s", "f", "c"],
    ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "title"]
  }).replaceAll("<br />", " \n").replaceAll("<br/>", "\n");
}
function _temp11(content_0) {
  if (!DOMPurify) {
    return content_0;
  }
  return DOMPurify.sanitize(content_0, {
    FORBID_TAGS: ["url", "@", "s", "f", "c"],
    ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "title"]
  });
}